import { Component, OnInit, OnDestroy, PLATFORM_ID, ViewChild, inject, NgZone } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  Router,
  RouterModule,
  NavigationEnd,
  ActivatedRoute,
} from '@angular/router';
import { BehaviorSubject, Subject, race, timer } from 'rxjs';
import { filter, takeUntil, debounceTime, take, distinctUntilChanged, skip } from 'rxjs/operators';

import {
  TabsComponent,
  TabItem,
} from '../../shared/components/tabs/tabs.component';
import {
  BannerCarouselComponent,
  BannerItem,
} from '../../shared/components/banner-carousel/banner-carousel.component';
import { ProductCardComponent } from '../../shared/components/product-card/product-card.component';
import { CarouselComponent } from '../../shared/components/carousel/carousel.component';
import { LoadingComponent } from '../../shared/components/loading/loading.component';
import { ProductV2Service } from '../../core/services/product-v2.service';
import {
  ProductV2,
  ProductV2Variation,
  SearchResultV2,
} from '../../core/models/product-v2.model';
import { ProductService } from '../../core/services/product.service';
import { ProductCategory } from '../../core/models/product.model';
import { CartFacade } from '../../core/store/cart/cart.facade';
import { NavigationFacade } from '../../core/store/navigation/navigation.facade';
import { AvailableOffersFacade } from '../../core/store/available-offers/available-offers.facade';
import { CartService } from '../../shared/services/cart.service';
import { HeaderService } from '../../shared/services/header.service';
import { sanitizeSlug } from '../../core/utils/utils';
import { GtmService, Ga4Item } from '../../core/services/gtm.service';
import { ErrorDisplayComponent } from '../../shared/components/error-display/error-display.component';
import { UserFacade } from '../../core/store/user/user.facade';
import { I18nService } from '../../core/services/i18n.service';
import { I18nPipe } from '../../shared/pipes/i18n.pipe';
import { ChannelEventsService } from '../../core/services/channel-events.service';
import { UManagerSlugService, ChannelData, ChannelMovelProduct } from '../../core/services/umanager-slug.service';
import { UtmPreservationService } from '../../core/services/utm-preservation.service';
import { ChannelThemeService } from '../../core/services/channel-theme.service';
import { ChannelConfigService } from '../../core/services/channel-config.service';
import { sortMindCatalogItems } from '../../core/store/mind/mind-catalog-group.util';
import { MindCatalogStore } from '../../core/store/mind/mind-catalog.store';
import { MindProductMapper } from '../../core/store/mind/mind-product.mapper';
import { CategoryHelperService } from '../../shared/services/category-helper.service';
import { environment } from '../../../environments/environment';
import { buildMarketplaceHomeBanners, inAppRouteFromBannerUrl, resolveBannerClick } from '../../core/utils/channel-banners.util';
import { applyMindCatalogImages } from '../../core/utils/channel-content.util';
import { filterProductsForCardDisplay } from '../../core/utils/product-card-display.util';
import { CampaignProductRef, catalogForCampaign, categoriesForCampaign } from '../../core/campaign/campaign-contract';
import { CampaignScopeService } from '../../core/campaign/campaign-scope.service';
import { BlipGoContractService } from '../../core/services/blip-go-contract.service';
import { isBlipGoProduct } from '../../core/constants/blip-go.constants';

@Component({
  selector: 'umkt-home',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    TabsComponent,
    BannerCarouselComponent,
    ProductCardComponent,
    CarouselComponent,
    LoadingComponent,
    ErrorDisplayComponent,
    I18nPipe
  ],
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
})
export class HomeComponent implements OnInit, OnDestroy {
  @ViewChild('carousel') carousel!: CarouselComponent;
  @ViewChild('tabsComponent') tabsComponent!: TabsComponent;

  banners: BannerItem[] = [];
  isBrowser: boolean = false;
  private resizeListener: (() => void) | null = null;
  private resetCarouselHandler: (() => void) | null = null;
  tabs: TabItem[] = [];
  products: ProductV2[] = [];
  categories: ProductCategory[] = [];
  activeTabIdSubject = new BehaviorSubject<string>('');
  cartFacade = inject(CartFacade);
  navigationFacade = inject(NavigationFacade);
  availableOffersFacade = inject(AvailableOffersFacade);
  private headerService = inject(HeaderService);
  private i18nService = inject(I18nService);

  private channelEventsService = inject(ChannelEventsService);
  private umanagerSlugService = inject(UManagerSlugService);
  private utmPreservationService = inject(UtmPreservationService);
  private mindStore = inject(MindCatalogStore);
  private blipGoContractService = inject(BlipGoContractService);

  showNoResultsMessage = false;
  isLoading = true;
  isCategoryLoading = false;
  error: string | null = null;
  private shouldScrollAfterSearch = false;
  private isSearchInProgress = false;
  retryCount = 0;
  private categoriesLoaded = false;
  private productsLoaded = false;
  private destroy$ = new Subject<void>();
  private hasSentInitialScreenView = false;
  private channelData: ChannelData | null = null;
  private previousUtmSource: string | null = null;
  private previousCampaignCode: string | null = null;

  private rawCategories: ProductCategory[] = [];
  private reloadCategories$ = new Subject<void>();  

  private readonly categoryKeyMap: Record<string, string> = {
    'Produtividade e Gestão': 'collaboration',
    'Presença Digital': 'digitalPresence',
    'Segurança': 'security',
    // 'Cloud': 'cloud',
    'Gestão e Benefícios Empresariais': 'management'
  };
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly productV2Service = inject(ProductV2Service);
  private readonly productService = inject(ProductService);
  private platformId = inject(PLATFORM_ID);
  private cartService = inject(CartService);
  private ngZone = inject(NgZone);
  public gtm = inject(GtmService);
  private userFacade = inject(UserFacade);
  private channelThemeService = inject(ChannelThemeService);
  private channelBrandService = inject(ChannelConfigService);
  private categoryHelper = inject(CategoryHelperService);
  private campaignScope = inject(CampaignScopeService);
  private campaignRefs: CampaignProductRef[] | null = null;
  private campaignCategoryUuids: string[] | null = null;

  constructor() {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnInit() {
    this.reloadCategories$
    .pipe(
      debounceTime(50),
      takeUntil(this.destroy$)
    )
    .subscribe(() => {
      this.loadCategoriesByCatalog();
    });

    this.i18nService.getCurrentLanguage()
    .pipe(
      distinctUntilChanged(),
      skip(1),
      takeUntil(this.destroy$)
    )
    .subscribe(() => {
      if (this.rawCategories.length > 0) {
        this.scheduleTabsTranslationWhenI18nReady();
      }

      this.productService.clearCategoriesCache();
      this.categoriesLoaded = false;
      this.productsLoaded = false;
      this.isLoading = true;
      
      this.activeTabIdSubject.next('');
      this.reloadCategories$.next();
    })

    this.blipGoContractService.hasContractedProduct$
      .pipe(distinctUntilChanged(), skip(1), takeUntil(this.destroy$))
      .subscribe(hasContracted => {
        if (hasContracted) {
          if (this.products?.length) {
            this.products = this.products.filter(product => !isBlipGoProduct(product));
            this.showNoResultsMessage = this.products.length === 0;
          }
          return;
        }

        const currentCategory = this.activeTabIdSubject.value;
        if (currentCategory) {
          this.activeTabIdSubject.next(currentCategory);
        }
      });

    this.mindStore.catalog$
    .pipe(
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    )
    .subscribe(catalog => {

      if (!this.channelData) return;

      const catalogs = this.getChannelCatalogs();
      const hasMind = catalogs.includes('MIND');
      const hasSva = catalogs.includes('SVA');

      if (!hasMind) return;

      if (catalog === null && hasMind && !hasSva) {
        this.applyCategories([]);
        return;
      }
      this.reloadCategories$.next();
    });
  
    if (this.isBrowser) {
      this.resizeListener = () => this.updateBanners();
      window.addEventListener('resize', this.resizeListener);
    }

    Promise.all([this.checkAndLoadChannelData(), this.loadCampaignCatalog()]).then(() => {
      this.updateBanners();
      this.loadCategoriesByCatalog();
      this.previousUtmSource = this.getCurrentUtmSource();
    });

    this.campaignScope.revision$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.loadCampaignCatalog().then(() => this.loadCategoriesByCatalog());
    });

    // Listen to query params changes to detect UTM removal/addition
    this.route.queryParams.pipe(
      distinctUntilChanged((prev, curr) => {
        const prevUtm = prev['utm_source'] || null;
        const currUtm = curr['utm_source'] || null;
        return prevUtm === currUtm && (prev['campaign'] || null) === (curr['campaign'] || null);
      }),
      debounceTime(100),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.handleUtmChange();
    });

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      filter((event: NavigationEnd) => {
        const url = event.url.split('?')[0];
        return url === '/';
      }),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.userFacade.getUserUuid().pipe(take(1)).subscribe(uuid => {
        if (uuid) {
          this.userFacade.loadUserDetails(uuid);
        }
      });

      // Small delay to ensure query params are updated
      setTimeout(() => {
        this.handleUtmChange();
      }, 100);
    });
    this.activeTabIdSubject
      .pipe(
        debounceTime(300),
        takeUntil(this.destroy$)
      )
      .subscribe(tabId => {
        if (tabId && tabId !== '') {
          this.getProductsByCategory();
        }
      });

    this.headerService.searchTerm$
      .pipe(
        debounceTime(100),  // Aguarda apenas 100ms
        takeUntil(this.destroy$)
      )
      .subscribe(searchTerm => {
        if (searchTerm) {
          this.performSearch(searchTerm);
        } else {
          // Quando o usuário apaga o termo de busca, volta para navegação normal
          if (this.isSearchInProgress) {
            this.clearSearchResults();
          }
        }
      });

    this.headerService.scrollTarget$
      .pipe(takeUntil(this.destroy$))
      .subscribe(target => {
        if (target) {
          setTimeout(() => {
            this.scrollToCategory(target);
          }, 600);
        }
      });

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event: NavigationEnd) => {
        const urlPath = event.url.split('?')[0];
        if (urlPath === '/' || this.isCategoryRoute(urlPath)) {
          setTimeout(() => {
            this.handleUrlBasedCategory();
          }, 150);
        }
      });

    this.route.queryParams.pipe(
      takeUntil(this.destroy$)
    ).subscribe(params => {
      if (params['scrollToProducts'] === 'true') {
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { scrollToProducts: null },
          queryParamsHandling: 'merge'
        });
        setTimeout(() => this.scrollToProductCarousel(), 300);
      }
    });

    if (this.isBrowser) {
      this.resetCarouselHandler = () => {
        this.resetCarouselToFirst();
      };
      window.addEventListener('resetCarousel', this.resetCarouselHandler);
    }
  }

  private isCategoryRoute(url: string): boolean {
    const urlSegments = url.substring(1).split('/');
    if (urlSegments.length === 1 && urlSegments[0] !== '') {
      return true;
    }
    return false;
  }

  private checkLoadingComplete(): void {
    if (this.categoriesLoaded && this.productsLoaded) {
      this.isLoading = false;
    }
    if (this.error) {
      this.isLoading = false;
    }
  }

  private getProductsMind(): void {
    this.isCategoryLoading = true;
    this.productsLoaded = false;

    const currentCategory: any = this.getCurrentCategory();
    const mindKey = currentCategory?.mindKey;

    this.mindStore.catalog$
      .pipe(take(1))
      .subscribe(catalog => {
        if (!catalog?.groups?.length) {
          this.products = [];
          this.productsLoaded = true;
          this.isCategoryLoading = false;
          return;
        }

        const group = catalog.groups.find(g => g.groupId === mindKey);

        if (!group) {
          this.products = [];
          this.productsLoaded = true;
          this.isCategoryLoading = false;
          return;
        }

        const mappedProducts = sortMindCatalogItems(group.items).map(item =>
          MindProductMapper.mapItemToProduct(item, mindKey, group.title)
        );

        this.products = this.limitToCampaign(
          applyMindCatalogImages(mappedProducts, this.channelBrandService.mindCatalogImageUrls)
        );
        this.productsLoaded = true;
        this.isCategoryLoading = false;
        this.checkLoadingComplete();
      });
  }

  private getProductsMovel(): void {
    const slug = this.channelData?.slug || this.getCurrentUtmSource();
    if (!slug) {
      this.products = [];
      this.showNoResultsMessage = true;
      this.productsLoaded = true;
      this.isCategoryLoading = false;
      this.checkLoadingComplete();
      return;
    }

    const requestCategory = this.activeTabIdSubject.value;
    this.isCategoryLoading = true;
    this.productsLoaded = false;
    this.error = null;

    this.umanagerSlugService
      .getChannelProducts(slug)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: products => {
          if (this.activeTabIdSubject.value !== requestCategory) {
            return;
          }

          this.products = this.limitToCampaign(this.mapMovelProducts(products));
          this.showNoResultsMessage = this.products.length === 0;
          this.productsLoaded = true;
          this.isCategoryLoading = false;
          this.checkLoadingComplete();

          const categoryName = requestCategory;
          const items: Ga4Item[] = (this.products || []).map((p, idx) => ({
            item_id: p.sku || p.uuid || String(p.id),
            item_name: p.name,
            item_category: categoryName,
            index: idx + 1,
            price: p.salesPrice ?? p.price ?? 0,
          }));
          this.gtm.viewItemList(categoryName, categoryName, items);
        },
        error: err => {
          console.error('Error fetching channel movel products:', err);
          this.error = 'Erro ao carregar produtos.';
          this.products = [];
          this.productsLoaded = true;
          this.isCategoryLoading = false;
          this.checkLoadingComplete();
        },
      });
  }

  private mapMovelProducts(products: ChannelMovelProduct[]): ProductV2[] {
    return (products ?? []).map((product, index) =>
      ({
        id: index,
        uuid: product.uuid || `movel-${index}`,
        name: product.title || '',
        excerpt: product.description || '',
        description: product.description || '',
        productType: 'MOVEL',
        billingModel: '',
        sku: product.uuid || `movel-${index}`,
        price: Number(product.price) || 0,
        salesPrice: Number(product.price) || 0,
        salesPrices: [],
        currency: 'BRL',
        termsUrl: '',
        terms: '',
        links: [],
        active: true,
        featured: false,
        image: null,
        imageUrl: product.image || '',
        imageUuid: '',
        plan: false,
        paymentRecurrence: '',
        customFields: [],
        categories: [],
        tags: [],
        variations: [],
        requiredFields: [],
        galleryImages: [],
        link: product.link || '',
        deleted: false,
        createdAt: product.createdAt || '',
      }) as ProductV2
    );
  }

  onCategoryChange(category: string) {
    this.showNoResultsMessage = false;
    
    // Send CATEGORY_VIEWED event with category name
    this.channelEventsService.sendCategoryViewedEvent(category).subscribe({
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      next: () => {},
      error: (err) => console.error('Error sending CATEGORY_VIEWED event:', err)
    });

    this.activeTabIdSubject.next(category);
    setTimeout(() => {
      if (this.carousel) {
        this.carousel.resetPosition();
      }
    }, 0);
  }

  clearSearchResults(): void {
    this.showNoResultsMessage = false;
    this.isSearchInProgress = false;
    
    if (this.categories.length > 0) {
      const firstCategory = this.categories[0];
      this.activeTabIdSubject.next(firstCategory.name);
      
      setTimeout(() => {
        if (this.tabsComponent) {
          this.tabsComponent.resetToBeginning();
        }
      }, 100);
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    
    if (this.isBrowser) {
      if (this.resizeListener) {
        window.removeEventListener('resize', this.resizeListener);
      }
      if (this.resetCarouselHandler) {
        window.removeEventListener('resetCarousel', this.resetCarouselHandler);
      }
    }
  }

  private scrollToCategory(name: string) {
    if (!this.categoriesLoaded || this.categories.length === 0) {
      setTimeout(() => this.scrollToCategory(name), 200);
      return;
    }
    
    let category = this.categories.find(
      x => x.name.toLowerCase() === name.toLowerCase()
    );

    if (!category) {
      category = this.categories.find(x => sanitizeSlug(x.name) === name);
    }

    if (category) {
      if (this.activeTabIdSubject.value !== category.name) {
        this.activeTabIdSubject.next(category.name);
      }
      setTimeout(() => {
        this.scrollToCategorySection();
      }, 300);
    }
  }

  private scrollToCategorySection() {
    const headerHeight = 100;
    const offset = 100;
    
    this.waitForElement('category-item').then(element => {
      const parentEl = element.parentElement;
      if (parentEl) {
        const elementPosition = parentEl.getBoundingClientRect().top;
        const offsetPosition =
          elementPosition + window.pageYOffset - headerHeight - offset;
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth',
        });
      }
    });
  }

  private scrollToProductCarousel() {
    const headerHeight = 100;
    const offset = 100;
    
    this.waitForElement('category-item').then(element => {
      const parentEl = element.parentElement;
      if (parentEl) {
        const elementPosition = parentEl.getBoundingClientRect().top;
        const offsetPosition =
          elementPosition + window.pageYOffset - headerHeight - offset;
        window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
        setTimeout(() => this.resetCarouselToFirst(), 400);
      }
    });
  }

  private resetCarouselToFirst() {
    if (this.carousel) {
      this.carousel.resetPosition();
    }
  }

  private waitForElement(id: string): Promise<HTMLElement> {
    return new Promise(resolve => {
      const check = () => {
        const el = document.getElementById(id);
        if (el) {
          resolve(el);
        } else {
          requestAnimationFrame(check);
        }
      };
      this.ngZone.runOutsideAngular(() => check());
    });
  }

  private updateBanners() {
    const isMobile = this.isMobileDevice;
    this.banners = buildMarketplaceHomeBanners(this.channelData, isMobile, this.i18nService);

    const promotions = this.banners.map(b => ({
      promotion_id: b.id,
      promotion_name: b.title,
      creative_name: b.title,
      creative_slot: 'home_banner',
    }));
    this.gtm.push('view_promotion', { items: promotions });
  }

  private get isMobileDevice(): boolean {
    if (!this.isBrowser) return false;
    return window.innerWidth <= 768;
  }

  getCurrentCategory(): ProductCategory | undefined {
    return this.categories.find(
      tab => tab.name === this.activeTabIdSubject.value
    );
  }

  getProductsByCategory(): void {
    const categoryName = this.activeTabIdSubject.value;
    if (!categoryName || categoryName === '') return;

    const currentCategory: any = this.getCurrentCategory();
    const requestCategory = categoryName;

    if (this.channelData && currentCategory?.catalogType === 'MIND') {
      this.getProductsMind();
      return;
    }

    if (this.channelData && currentCategory?.catalogType === 'MOVEL') {
      this.getProductsMovel();
      return;
    }

    const catalogs = this.getChannelCatalogs();
    if (this.channelData && !catalogs.includes('SVA')) {
      this.products = [];
      this.isCategoryLoading = false;
      return;
    }
    this.isCategoryLoading = true;
    this.productsLoaded = false;

    this.productV2Service
      .getProductsByCategory(categoryName)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: resp => {

          if (this.activeTabIdSubject.value !== requestCategory) {
            return; 
          }
          let products = resp.data;

          // Filter products by channel offersUuid if channel data is available
          if (this.channelData?.offersUuid?.length) {
            products = products.filter(product =>
              this.channelData!.offersUuid.includes(product.uuid)
            );
          }

          products = this.limitToCampaign(products);

          this.products = filterProductsForCardDisplay(products, {
            hideBlipGo: this.blipGoContractService.shouldHideBlipGo(),
          });
          this.showNoResultsMessage = this.products.length === 0;

          if (this.products?.length) {
            this.availableOffersFacade.mapProductsVariationsToOffers(this.products);
          }

          this.productsLoaded = true;
          this.isCategoryLoading = false;
          this.checkLoadingComplete();

          const items: Ga4Item[] = (this.products || []).map((p, idx) => ({
            item_id: p.sku || p.uuid || p.id,
            item_name: p.name,
            item_category: categoryName,
            index: idx + 1,
            price: p.salesPrice ?? p.price ?? 0,
          }));

          this.gtm.viewItemList(categoryName, categoryName, items);

          if (this.shouldScrollAfterSearch) {
            this.shouldScrollAfterSearch = false;
            setTimeout(() => {
              this.scrollToProductsSection();
            }, 200);
          }
        },
        error: _error => {
          console.error('Error fetching products by category:', _error);
          this.error = 'Erro ao carregar produtos.';
          this.productsLoaded = true;
          this.isCategoryLoading = false;
          this.checkLoadingComplete();
        },
      });
  }

  private getCurrentUtmSource(): string | null {
    if (!this.isBrowser) {
      return null;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const utmSourceFromUrl = urlParams.get('utm_source');
    
    if (utmSourceFromUrl) {
      return utmSourceFromUrl;
    }
    
    // If UTM temporarily drops from URL (e.g. navigation to /login without query params),
    // fall back to preserved params to keep the experience consistent across flows.
    return this.utmPreservationService.preservedUtmParams?.['utm_source'] || null;
  }

  private handleUtmChange(): void {
    const currentUtmSource = this.getCurrentUtmSource();
    const currentCampaign = this.currentCampaignCode();
    const campaignChanged = currentCampaign !== this.previousCampaignCode;

    if (currentUtmSource !== this.previousUtmSource || campaignChanged) {
      if (this.previousUtmSource && !currentUtmSource) {
        this.channelData = null;
      }

      const shouldReloadCategories = campaignChanged || !this.categoriesLoaded ||
                                    (this.previousUtmSource && !currentUtmSource);

      Promise.all([this.checkAndLoadChannelData(), this.loadCampaignCatalog()]).then(() => {
        if (shouldReloadCategories) {
          this.loadCategoriesByCatalog();
        } else if (this.channelData) {
          this.loadCategoriesByCatalog();
        }
      });

      this.previousUtmSource = currentUtmSource;
      this.previousCampaignCode = currentCampaign;
    }
  }

  private currentCampaignCode(): string | null {
    if (!this.isBrowser) {
      return null;
    }
    const query = (new URLSearchParams(window.location.search).get('campaign') ?? '').trim();
    return query || null;
  }

  private getChannelCatalogs(): string[] {
    if (!this.channelData?.catalogs) return [];
    return this.channelData.catalogs.map((c: string) => c.toUpperCase());
  }

  private buildMovelCategory(): any {
    return {
      id: 0,
      name: 'Móvel',
      description: '',
      uuid: 'movel',
      deleted: false,
      hasProducts: true,
      catalogType: 'MOVEL',
    };
  }

  private prependMovelCategory(categories: any[] = []): any[] {
    if (!this.getChannelCatalogs().includes('MOVEL')) {
      return categories;
    }

    const withoutMovel = categories.filter(
      category => category?.catalogType !== 'MOVEL' && category?.uuid !== 'movel'
    );
    return [this.buildMovelCategory(), ...withoutMovel];
  }

  private loadCategoriesByCatalog(): void {
    this.isLoading = true; 
    
    if (!this.channelData) {
      this.getCategories();
      return;
    }

    const catalogs = this.getChannelCatalogs();
    const hasSva = catalogs.includes('SVA');
    const hasMind = catalogs.includes('MIND');

    if (hasMind && !hasSva) {
      this.getCategoriesMind();
      return;
    }

    if (hasSva && hasMind) {
      this.getCategoriesCombined();
      return;
    }

    if (hasSva) {
      this.getCategories();
      return;
    }

    this.applyCategories([]);
  }

  getCategoriesCombined(): void {
    this.productService.getCategories()
    .pipe(takeUntil(this.destroy$))
    .subscribe({
      next: resp => {
        let svaCategories = resp.data;
        
        if (this.channelData?.categoriesUuid?.length) {
          svaCategories = svaCategories.filter(cat =>
            this.channelData!.categoriesUuid.includes(cat.uuid)
          );
        }

        const buildMindCategories = () => {
          const catalog = this.mindStore.snapshot;
          return (catalog?.groups ?? [])
            .filter(group => group.items && group.items.length > 0)
            .map(group => ({
              name: group.title,
              uuid: `${group.groupId}`,
              hasProducts: true,
              catalogType: 'MIND',
              mindKey: group.groupId
            }));
        };

        const applyWithMindFirst = () => {
          const mindCategories = buildMindCategories();
          this.applyCategories([...mindCategories, ...svaCategories]);
        };

        // manter o home em loading enquanto as categorias do mind ainda estão carregando
        const mindSnapshotHasGroups = !!(this.mindStore.snapshot?.groups?.length);
        if (mindSnapshotHasGroups) {
          applyWithMindFirst();
          return;
        }

        // esperar pelo catalogo do mind ou timeout de 2500ms
        this.isLoading = true;
        this.categoriesLoaded = false;
        this.productsLoaded = false;

        race(
          this.mindStore.catalog$.pipe(
            filter(catalog => !!catalog && !!catalog.groups),
            take(1)
          ),
          timer(2500).pipe(take(1))
        )
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => applyWithMindFirst());
      },
      error: err => {
        console.error(err);
        this.error = 'Erro ao carregar categorias.';
      }
    });
  }

  private applyCategories(categories: any[] = []): void {
    const nextCategories = categoriesForCampaign(
      this.prependMovelCategory(categories),
      this.campaignCategoryUuids
    );
    this.rawCategories = [...nextCategories];
    this.categories = nextCategories;

    if (nextCategories.length === 0) {
      this.tabs = [];
      this.products = [];
      this.showNoResultsMessage = true;
      this.categoriesLoaded = true;
      this.productsLoaded = true;
      this.isLoading = false;
      this.isCategoryLoading = false;
      return;
    }

    this.tabs = this.categories.map(category => ({
      id: category.name,
      label: category.name,
      disabled: false,
    }));

    this.showNoResultsMessage = false;
    this.categoriesLoaded = true;
    this.checkLoadingComplete();

    this.scheduleTabsTranslationWhenI18nReady();
    this.handleUrlBasedCategory();
  }

  private getCategoriesMind(): void {
    this.isLoading = true; 

    this.mindStore.catalog$
      .pipe(
        filter(catalog => !!catalog && !!catalog.groups), 
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (catalog) => {
          const mindCategories = catalog?.groups
            .filter(group => group.items && group.items.length > 0)
            .map(group => ({
              name: group.title,
              description: '',
              uuid: `${group.groupId}`,
              hasProducts: true,
              deleted: false,
              catalogType: 'MIND',
              mindKey: group.groupId
            }));

          this.applyCategories(mindCategories);
        },
        error: () => {
          this.applyCategories([]);
        }
      });
  }

  private loadCampaignCatalog(): Promise<void> {
    if (!this.isBrowser) {
      this.campaignRefs = null;
      this.campaignCategoryUuids = null;
      return Promise.resolve();
    }

    return this.campaignScope.load().then(scope => {
      this.campaignRefs = scope?.products ?? null;
      this.campaignCategoryUuids = scope?.categoryUuids.length ? scope.categoryUuids : null;
    });
  }

  private limitToCampaign<T extends { uuid: string; variations?: { uuid: string }[] }>(products: T[]): T[] {
    return catalogForCampaign(products, this.campaignRefs);
  }

  private checkAndLoadChannelData(): Promise<void> {
    return new Promise((resolve) => {
      if (!this.isBrowser) {
        this.channelData = null;
        this.channelBrandService.setChannelData(null);
        resolve();
        return;
      }

      // IMPORTANT: Only apply channel filtering if utm_source is present in the URL.
      // If utm_source is removed from the URL, we must clear filters and stop calling uManager.
      const urlParams = new URLSearchParams(window.location.search);
      let utmSource = urlParams.get('utm_source');

      // If UTM is not in URL yet (e.g. login redirects), fall back to preserved params.
      if (!utmSource) {
        utmSource = this.utmPreservationService.preservedUtmParams?.['utm_source'] || null;
      }

      if (!utmSource) {
        this.channelData = null; // Ensure channelData is null when no utm_source
        this.channelThemeService.resetToDefault();
        this.channelBrandService.setChannelData(null);
        resolve();
        return;
      }

      // Check if slug exists
      this.umanagerSlugService.checkSlugExists(utmSource).pipe(
        takeUntil(this.destroy$),
        take(1)
      ).subscribe({
        next: (response) => {
          if (response?.exists && utmSource) {
            // Slug exists, get channel data
            this.umanagerSlugService.getChannelBySlug(utmSource).pipe(
              takeUntil(this.destroy$),
              take(1)
            ).subscribe({
              next: (channelData) => {
                if (channelData?.status && channelData.status !== 'ACTIVE') {
                  this.channelData = null;
                  this.utmPreservationService.disableUtmAndCleanUrl();
                  this.channelThemeService.resetToDefault();
                  this.channelBrandService.setChannelData(null);
                  resolve();
                  return;
                }

                this.channelData = channelData || null;
                const parsedColors = this.channelThemeService.parseChannelColors(
                  channelData?.colors ?? null
                );
                if (parsedColors) {
                  this.channelThemeService.applyChannelColors(parsedColors);
                } else {
                  this.channelThemeService.resetToDefault();
                }
                this.channelBrandService.setChannelData(channelData);
                this.updateBanners();
                resolve();
              },
              error: (error) => {
                console.error('Error loading channel data:', error);
                resolve();
              }
            });
          } else {
            console.log('Slug does not exist, channel filtering will not be applied');
            this.channelData = null;
            this.channelBrandService.setChannelData(null);
            resolve();
          }
        },
        error: (error) => {
          console.error('Error checking slug existence:', error);
          resolve();
        }
      });
    });
  }

  getCategories(): void {
    this.productService.getCategories().pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: resp => {

        const filteredCategories =
          this.categoryHelper.filterAndSortCategories(
            resp.data,
            this.channelData?.categoriesUuid
          );

        this.retryCount = 0;
        this.applyCategories(filteredCategories);
      },
      error: error => {
        console.error('Error fetching categories:', error);
        this.error = 'Erro ao carregar categorias.';
        this.categoriesLoaded = true;
        this.checkLoadingComplete();
      },
    });
  }

  private scheduleTabsTranslationWhenI18nReady(): void {
    if (!this.rawCategories.length) {
      return;
    }
    const locale = this.i18nService.getCurrentLanguageSync();
    this.i18nService
      .loadLanguage(locale)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(() => this.updateTabsTranslation());
  }

  private updateTabsTranslation(): void {
    if (!this.rawCategories.length) return;

    this.tabs = this.rawCategories.map(category => {
      const translationKey = this.getCategoryTranslationKey(category.name);
      const label = translationKey ? this.i18nService.instant(translationKey) : category.name;

      return {
        id: category.name, 
        label: label,
        disabled: false,
      };
    });
  }

  private getCategoryTranslationKey(categoryName: string): string | null {
    if (categoryName === 'Móvel') {
      return 'pages.home.movelCategory';
    }

    if (categoryName.startsWith('shared.categories.')) {
      return categoryName;
    }

    const keyId = this.categoryKeyMap[categoryName];
    return keyId ? `shared.categories.${keyId}` : null;
  }

  private handleUrlBasedCategory(): void {
    if (!this.categoriesLoaded || this.categories.length === 0 || this.isSearchInProgress) {
      return;
    }

    const url = this.router.url;
    const urlPath = url.split('?')[0];
    let selectedCategory: string;
    let shouldScroll = false;
    let shouldResetScroll = false;
    let shouldClearUrl = false;
    
    if (urlPath === '/') {
      selectedCategory = this.categories[0].name;
      shouldResetScroll = true;
    } else {
      const urlSegments = urlPath.substring(1).split('/');
      const categorySlug = urlSegments[0];
      
      let category = this.categories.find(
        x => x.name.toLowerCase() === categorySlug.toLowerCase()
      );

      if (!category) {
        category = this.categories.find(x => sanitizeSlug(x.name) === categorySlug);
      }

      if (category) {
        selectedCategory = category.name;
        shouldScroll = true;
      } else {
        selectedCategory = this.categories[0].name;
        shouldResetScroll = true;
        shouldClearUrl = true;
      }
    }
    
    if (shouldClearUrl) {
      const currentQueryParams: Record<string, string> = {};
      if (this.isBrowser) {
        const urlParams = new URLSearchParams(window.location.search);
        urlParams.forEach((value, key) => {
          currentQueryParams[key] = value;
        });
      } else {
        Object.assign(currentQueryParams, this.route.snapshot.queryParams);
      }
      
      const queryParams = this.utmPreservationService.addPreservedUtmToQueryParams({ ...currentQueryParams });
      
      this.router.navigate(['/'], {
        queryParams,
        replaceUrl: true
      });
    }
    
    // Só muda a categoria se for diferente da atual
    if (this.activeTabIdSubject.value !== selectedCategory) {
      this.activeTabIdSubject.next(selectedCategory);
      
      if (!this.hasSentInitialScreenView) {
        this.hasSentInitialScreenView = true;
        this.channelEventsService.sendCategoryViewedEvent(selectedCategory).subscribe({
          error: (err) => console.error('Error sending initial CATEGORY_VIEWED event:', err)
        });
      }
    }

    if (shouldScroll) {
      setTimeout(() => {
        this.scrollToCategory(selectedCategory);
      }, 600);
    } else if (shouldResetScroll) {
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    }
  }

  onLearnMore(product: ProductV2) {
    const categorySlug = sanitizeSlug(this.getCurrentCategory()?.name || '');
    const productSlug = sanitizeSlug(product.name);
    const isMind = product.productType === 'MIND';

    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);

    this.navigationFacade.setCurrentProduct(
      product.uuid,
      this.getCurrentCategory()?.name || ''
    );

    const queryParams = this.utmPreservationService.addPreservedUtmToQueryParams({
      ...(this.route.snapshot?.queryParams || {}),
    });

    this.router.navigate([categorySlug, productSlug], {
      queryParams,
      state: isMind ? { mindProductDetail: true } : undefined,
    });
  }

  onAddToCartFromCard(
    event: { plan?: ProductV2Variation | null; quantity: number },
    product: ProductV2
  ) {
    this.cartFacade.addToCart(product, event.quantity, event.plan || undefined);
  }

  handleRetry() {
    this.retryCount++;
    this.error = null;
    this.isLoading = true;
    this.categoriesLoaded = false;
    this.productsLoaded = false;

    this.loadCategoriesByCatalog();

    if (this.activeTabIdSubject.value) {
      // Trigger category loading via subscription
      this.activeTabIdSubject.next(this.activeTabIdSubject.value);
    }
  }

  performSearch(searchTerm: string): void {
    this.isSearchInProgress = true;
    
    this.productV2Service.searchProducts(searchTerm).subscribe({
      next: searchResults => {
        if (searchResults && searchResults.length > 0) {
          this.showNoResultsMessage = false;

          // Find the best matching category based on search term and results
          let categoryName = this.findBestMatchingCategoryFromSearch(searchTerm, searchResults);
          
          if (!categoryName) {
            // Fallback to first available category
            categoryName = this.categories[0]?.name || '';
          }

          this.isCategoryLoading = true;
          this.shouldScrollAfterSearch = true;

          // Set the category and load products (will trigger getProductsByCategory automatically)
          this.activeTabIdSubject.next(categoryName);

          setTimeout(() => {
            if (this.tabsComponent) {
              this.tabsComponent.scrollToActiveTabPublic();
            }
            this.isSearchInProgress = false;
          }, 150);
        } else {
          this.showNoResultsMessage = true;
          this.products = [];
          this.isCategoryLoading = false;
          this.isSearchInProgress = false;
          this.productsLoaded = true;
          this.checkLoadingComplete();

          setTimeout(() => {
            if (this.tabsComponent) {
              this.tabsComponent.resetToBeginning();
            }
            this.scrollToTop();
          }, 100);
        }
      },
      error: _error => {
        this.showNoResultsMessage = true;
        this.products = [];
        this.isCategoryLoading = false;
        this.isSearchInProgress = false;
        this.productsLoaded = true;
        this.checkLoadingComplete();

        setTimeout(() => {
          if (this.tabsComponent) {
            this.tabsComponent.resetToBeginning();
          }
          this.scrollToTop();
        }, 100);
      },
    });
  }

  private scrollToProductsSection(): void {
    const headerHeight = 100;
    const offset = 50;

    const categorySection = document.querySelector('.category-section');
    if (categorySection) {
      const elementPosition = categorySection.getBoundingClientRect().top;
      const offsetPosition =
        elementPosition + window.pageYOffset - headerHeight - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  }

  private scrollToTop(): void {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }

  private findBestMatchingCategoryFromSearch(searchTerm: string, searchResults: SearchResultV2[]): string | null {
    if (!searchResults.length) return null;
    
    // Busca produto que contém o termo de busca no productName E que a categoria existe
    const matchingProduct = searchResults.find(result => {
      const productName = result.productName.toLowerCase();
      const searchTermLower = searchTerm.toLowerCase();
      
      // Verifica se o produto contém o termo de busca
      if (!productName.includes(searchTermLower)) {
        return false;
      }
      
      // Verifica se a categoria do produto existe nas categorias disponíveis
      const category = this.categories.find(cat => 
        cat.name.toLowerCase() === result.productCategory.toLowerCase()
      );
      
      return !!category; // Retorna true apenas se a categoria existir
    });
    
    if (matchingProduct) {
      // Retorna a categoria do produto encontrado (já validada)
      return this.categories.find(cat => 
        cat.name.toLowerCase() === matchingProduct.productCategory.toLowerCase()
      )?.name || null;
    }
    
    // Fallback: pega a categoria do primeiro resultado que existe
    for (const result of searchResults) {
      const category = this.categories.find(cat => 
        cat.name.toLowerCase() === result.productCategory.toLowerCase()
      );
      if (category) {
        return category.name;
      }
    }
    
    return null;
  }

  onBannerClick(banner: BannerItem) {
    const action = resolveBannerClick(banner);
    if (action.type === 'external') {
      if (!this.isBrowser) {
        return;
      }
      const inAppRoute = inAppRouteFromBannerUrl(
        action.url,
        window.location.origin,
        environment.basePath
      );
      if (inAppRoute) {
        this.router.navigateByUrl(inAppRoute);
        return;
      }
      window.open(action.url, '_blank', 'noopener,noreferrer');
      return;
    }
    if (action.type === 'internal') {
      this.router.navigate([action.route]);
    }
  }

  get homeHeadline(): string | null {
    return this.channelBrandService.homeHeadline;
  }

  get homeSubheadline(): string | null {
    return this.channelBrandService.homeSubheadline;
  }
}
